import React from 'react';
import { ArrowRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function MappingStep({
  schema,
  parsed,
  columnMap,
  setColumnMap,
  mappedCount,
  mandatoryUnmapped,
}) {
  return (
    <div>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Column Mapping</h3>
          <p className="text-xs text-slate-500">Map each Excel column to its HGS system field</p>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
            {mappedCount}/{schema.fields.length} mapped
          </span>
          {mandatoryUnmapped > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-red-50 text-[#CC0000] border border-red-200 font-semibold">
              {mandatoryUnmapped} mandatory missing
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[1fr_56px_1fr] gap-3 mb-2 px-3">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Excel Column (your file)</div>
        <div />
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">HGS System Field</div>
      </div>

      <div className="space-y-2">
        {schema.fields.map((f) => {
          const idx = columnMap[f.key];
          const matched = idx != null && idx >= 0;
          const isMissing = f.mandatory && !matched;
          return (
            <div
              key={f.key}
              className={cn(
                'grid grid-cols-[1fr_56px_1fr] items-center gap-3 p-3 rounded-lg border transition',
                matched ? 'bg-emerald-50/40 border-emerald-200'
                  : isMissing ? 'bg-red-50/40 border-red-200'
                  : 'bg-white border-slate-200'
              )}
            >
              <select
                value={idx ?? -1}
                onChange={(e) => setColumnMap((m) => ({ ...m, [f.key]: parseInt(e.target.value, 10) }))}
                className="px-3 py-2 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10 font-medium text-slate-800"
              >
                <option value={-1}>— Not mapped —</option>
                {parsed.headers.map((h, i) => (
                  <option key={`map-opt-${i}-${h}`} value={i}>{h || `Col ${i + 1}`}</option>
                ))}
              </select>

              <div className="flex items-center justify-center">
                <ArrowRight className={cn('w-4 h-4', matched ? 'text-emerald-600' : isMissing ? 'text-[#CC0000]' : 'text-slate-300')} />
              </div>

              <div>
                <div className="text-xs font-bold text-slate-900 flex items-center gap-1">
                  {f.label}
                  {f.mandatory && <span className="text-[#CC0000]">*</span>}
                  {matched ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 ml-auto" />
                  ) : isMissing ? (
                    <AlertTriangle className="w-3 h-3 text-[#CC0000] ml-auto" />
                  ) : null}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[10px]">
                  <span className="font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{f.key}</span>
                  <span className="text-slate-500">{f.type}{f.options ? ` (${f.options.length} opts)` : ''}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
